<template>
  <div>
    <ExecutionResultsDialog title="Upload and auto-detect file type" ref="executionResultsDialog" @close="$emit('refresh')" @close_waring="closeAutoDetectResult()">
      <template #title="{ isError }">
        <span v-if="isError">
          <v-icon class="mr-1" large color="warning">warning</v-icon>
          File successfully uploaded, but we failed to detect its file type
        </span>
        <span v-else>
          <v-icon class="mr-1" large color="success">check_circle</v-icon>
          File successfully uploaded and its file type was detected
        </span>
      </template>
      <template #actions="{ FileResults }">
        <ul v-if="FileResults && FileResults.length > 0">
          <li class="pb-2">
            <router-link :to="{ name: 'dataset', params: {id: FileResults[0].FileId }}" target="_blank">Review the file</router-link> and its contents for any formatting mistakes. Reupload the file if any changes were made.
          </li>
          <li class="pb-2" v-if="isTenantAdmin">Customize the ingestion service configurations to better suit your file format.</li>
          <li class="pb-2" v-if="isTenantAdmin">
            <router-link :to="{ name: 'dataset', params: {id: FileResults[0].FileId }}" target="_blank">Manually set the file type</router-link> by editing the file.
          </li>
          <li class="pb-2">Contact your administrator or Hatfield if you require assistance.</li>
        </ul>
      </template>
      <template #next="{ FileResults }">
        <PrimaryBtn color="primary" :to="{ name: 'dataset', params: {id: FileResults[0].FileId }}">
          View uploaded file
        </PrimaryBtn>
      </template>
    </ExecutionResultsDialog>
    <v-dialog v-model="show" persistent max-width="750px" ref="addFileDialog">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ $t('DataSetAddFileDialog.addTitleMsg') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click.native="closeDialog()" dark>
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-progress-linear indeterminate v-if="isLoading"></v-progress-linear>
        <v-card-text>
          <v-stepper v-model="step" style="box-shadow: none">
            <v-stepper-content step="1">
              <v-layout wrap row justify-space-between align-space-around>
                <v-flex xs12 md12>
                  <v-form ref="form_step1" v-model="formStepOneIsValid">
                    <v-flex xs12 align-self-center>
                      <v-input>
                        <v-text-field v-model="filename" label="File Name" name="filename" :rules="fileNameRules">
                        </v-text-field>
                      </v-input>
                    </v-flex>
                    <v-flex xs12 align-self-center>
                      <v-input>
                        <v-select label="File Type" :disabled="isAutoDetectFileType" v-model="fileSchemaName" :items="schemaSelectItems" :rules="fileSchemaRules"></v-select>
                      </v-input>
                    </v-flex>
                    <v-flex v-if="genericTypeSchema" xs12 align-self-center>
                      <v-input>
                        <v-checkbox v-model="isAutoDetectFileType" color="primary" persistent-hint hint="Determines the file type based on its contents (currently supports A125 and Hatfield EDDs only)." label="Auto-detect file type"></v-checkbox>
                      </v-input>
                    </v-flex>
                    <v-flex v-else xs12 align-self-center>
                      <span>
                        <v-icon class="mr-1" large color="warning">warning</v-icon>
                        Auto-detect is not available because 'Generic' file schema does not exist. 
                      </span>
                    </v-flex>

                    <v-input style="display:none;">
                        <v-checkbox disabled v-model="isUploadCompleted" name="isUploadCompleted" :rules="isUploadCompletedRules">
                        </v-checkbox>
                    </v-input>
                    <v-flex xs12 grow align-self-center>
                      <v-alert class="ma-0 mb-2" color="warning lighten-2" icon="warning" outline>
                        <span class="primary--text py-2">
                          {{ $t('DataSetAddFileDialog.fileSizeLimitWarningMsg') }}
                        </span>
                      </v-alert>
                      <v-input>
                        <div @click.stop.prevent="uploadPrepare($event)" style="width:100%;">
                        <!-- <input type="file" id="file-import" ref="file-import" name="file-import" @input="handleFileInput" /> -->
                          <uploader ref="myUpload" end-point="api/RawDataRepository/UpoadFile"
                          :key="componentKey"
                          :disabled="isUploading"
                          :showProgressBar="true"
                          :multipart="true"
                          :showErrors="false"
                          :maxFileSize="maxFileSize"
                          :fileSizeExceededMsg="$t('DataSetAddFileDialog.fileSizeExceededMsg')"
                          :headers="{uploadFileIdentifier: uploadFileIdentifier}"
                          @fileUploaded="fileUploaded"
                          @error="uploadFailed"
                          @startUpload="startUpload">
                            <div slot="browse-btn" style="display:flex;width:100%;" >
                                <v-btn :disabled="false" color="blue-grey">
                                  Upload<v-icon rightdark>mdi-cloud-upload</v-icon>
                                </v-btn>
                                <v-text-field v-if="selectedFileName != null" :value="selectedFileName" disabled label="Selected File Name"></v-text-field>
                            </div>
                          </uploader>
                        </div>          
                      </v-input>
                      <v-alert :value="isUploading" class="ma-0" color="warning lighten-2" icon="warning" outline>
                        <span class="primary--text py-2">
                          {{ $t('DataSetAddFileDialog.uploadingTipsMsg') }}
                        </span>
                      </v-alert>
                      <v-alert :value="isUploadCompleted" class="ma-0" color="success lighten-2" icon="check_circle" outline>
                        <span class="primary--text py-2">
                          {{ $t('DataSetAddFileDialog.uploadedTipsMsg') }}
                        </span>
                      </v-alert>
                    </v-flex>
                  </v-form>
                </v-flex>
              </v-layout>
            </v-stepper-content>
            <v-stepper-content step="2">
              <v-form v-model="formStepTwoIsValid" ref="form_step2">
                <data-set-file-metadata-inputs v-model="metadataFields" :schema="fileSchemaObject" :disableAddButton="!formStepTwoIsValid" ref="metadata-inputs" @input="emitMetadataInput"/>
              </v-form>
            </v-stepper-content>
          </v-stepper>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" @click="proceed()" :disabled="!isValidForm">
            <v-icon>done</v-icon>{{ step == 1 ? $t('Button.Next') : $t('Button.Create')}}
          </v-btn>
          <v-btn outline color="secondary" @click="closeDialog()">{{ $t('Button.Close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="exitConfirmDialog" persistent max-width="600">
      <v-card>
        <v-toolbar dense dark color="primary">
          <v-toolbar-title>{{ $t('DataSetAddFileDialog.exitDialogTitleMsg') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="exitConfirmDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          {{ isUploading ? $t('DataSetAddFileDialog.exitDialogUploadingTipsMsg') : isUploadCompleted ? $t('DataSetAddFileDialog.exitDialogUploadCompletedTipsMsg') : '' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="delete" dark @click="cleanUpUploadedFile()">
            {{ $t('Button.Confirm') }}
          </v-btn>
          <v-btn @click="exitConfirmDialog = false">{{ $t('Button.Cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import FormValidationUtil from '@/components/mixins/FormValidationUtil'
  import MetadataUtil from '@/components/mixins/MetadataUtil'
  import ReactivityUtil from '@/components/mixins/ReactivityUtil'

  import DatasetFileMetadataInputs from './DatasetFileMetadataInputs'

  import {
    createNamespacedHelpers,
  } from 'vuex'
  import uploader from '@/components/generic/VuejsUploader/Uploader'
  const app = createNamespacedHelpers('app')
  const authenticate = createNamespacedHelpers('authenticate')

  const rawdatarepository = createNamespacedHelpers('rawdatarepository')
  const ingestions = createNamespacedHelpers('ingestions')
  const GENERIC_TYPE = 'Generic'

  export default {
    name: 'data-set-add-file',
    model: {
      prop: 'show',
      event: 'addDialogStatus',
    },
    components: {
      'data-set-file-metadata-inputs': DatasetFileMetadataInputs,
      uploader,
    },
    mixins: [FormValidationUtil, MetadataUtil, ReactivityUtil], 
    props: {
      show: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        step: 1,
        filename: '',
        fileSchemaName: '',
        fileSchemaObject: {},
        fileNameRules: [v => !!v || 'Please enter file name'],
        fileSchemaRules: [v => !!v || 'Please enter file type'],
        isUploadCompletedRules: [v => !!v || 'Please upload file'],
        formData: new FormData(),
        formStepOneIsValid: true,
        formStepTwoIsValid: true,
        isPostingRequest: false,
        metadataFields: [],
        uploadFileIdentifier: null,
        isUploading: false,
        isUploadCompleted: false,
        selectedFileName: null,
        componentKey: null,
        exitConfirmDialog: false,
        isFileNameAutoFill: true,
        isAutoDetectFileType: false,
        maxFileSize: 10 * 1024 * 1024,
      }
    },
    computed: {
      ...rawdatarepository.mapGetters([
        'metadataSchemas',
        'metadataSchemaByName',
      ]),
      ...authenticate.mapGetters(['isTenantAdmin']),
      isLoading() {
        return this.metadataSchemas.isLoading || this.isPostingRequest
      },
      schemaSelectItems() {
        return this.metadataSchemas.data.map(schema => {
          return {
            text: schema.Name,
            value: schema.Name,
          }
        })
      },
      genericTypeSchema() {
        return this.schemaSelectItems.find(ms => ms.value == GENERIC_TYPE)
      },
      isValidForm() {
        // file input events arent triggering render

        return this.step < 2 ?
          this.formStepOneIsValid :
          this.formStepTwoIsValid
      },
      formFields() {
        let that = this
        let fields = []
        fields = fields.concat(
          that.mandatoryMetadataFields.filter(that.isNonNullMetadataField)
        )
        fields = fields.concat(
          that.additionalMetadataFields.filter(
            that.isNonNullMetadataField
          )
        )
        return fields.map(f => {
          return {
            Field: f.Field,
            FieldType: f.FieldType,
            FieldValue: f.FieldValue,
          }
        })
      },
    },
    watch: {
      filename(v) {
        this.isFileNameAutoFill = (v == this.selectedFileName || v == '') ? true : false
      },
      fileSchemaName(name) {
        if (name != (this.genericTypeSchema && this.genericTypeSchema.value)) this.isAutoDetectFileType = false
        this.fileSchemaObject = this.metadataSchemaByName(name)
      },
      isAutoDetectFileType(val) {
        if (val && this.genericTypeSchema) {
          this.fileSchemaName = this.genericTypeSchema && this.genericTypeSchema.value
        }
      },

    },
    methods: {
      ...app.mapActions(['setSnackbar']),
      ...rawdatarepository.mapActions(['addFile', 'getMetadataSchemas', 'getUploadIdentifier', 'uploadCancel']),
      ...ingestions.mapActions(['checkFileFormat']),
      clearForm() {
        this.step = 1
        this.filename = ''
        this.filetype = ''
        this.$refs['form_step1'].reset()
        this.$refs['form_step1'].resetValidation()
        this.formData = new FormData()
        
        //this.$refs['file-import'].value = null
        this.$refs['metadata-inputs'].reset()
        this.$refs['form_step2'].reset()
        this.$refs['form_step2'].resetValidation()
        this.resetUploadComponent()
        this.exitConfirmDialog = false
        this.$emit('addDialogStatus', false)
      },
      proceed() {
        if (this.step != 2) {
          this.step = 2
        } else {
          this.submit()
        }
      },
      async submit() {
        this.constructAddFileFormData()        
        this.isPostingRequest = true
        try {
          let res = await this.addFile(this.formData)
          if (this.isAutoDetectFileType) {
            await this.autoDetectFileType(res.Id)
          } 
          else {
            this.setSnackbar({
              color: 'success',
              text: 'File imported successfully.',
            })
            this.clearForm()
          }
        } catch (err) {
          console.error(`Error occurred posting file ${err}`)
          this.setSnackbar({
            color: 'error',
            text: `Error occurred when adding file: ${err}`,
          })
        } finally {
          this.isPostingRequest = false
          this.$emit('refresh')
        }
      },
      async autoDetectFileType(rdrFileId) {
        try {
          let res = await this.checkFileFormat({ rdrFileId })
          this.$refs.executionResultsDialog.show(res)
        } catch (err) {
          this.setSnackbar({
            color: 'warning',
            text: `File successfully uploaded, but file type auto-detection encountered an error. ${err}`,
          })
        }
      },
      constructAddFileFormData() {        
        this.formData.append('filename', this.filename)
        // this.formData.append(
        //   'file-import',
        //   this.$refs['file-import'].files[0]
        // )
        this.formData.append('uploadFileIdentifier', this.uploadFileIdentifier)
        this.formData.append('metadata-schema', this.fileSchemaName)
        this.formData.append('isAutoDetect', this.isAutoDetectFileType)

        let expandedMetadataFields = this.expandGroupedFields(
          this.metadataFields
        )
        this.formData.append(
          'metadata-fields',
          JSON.stringify(expandedMetadataFields)
        )        
      },
      emitNotification(statusType, message) {
        this.setSnackbar({
          color: statusType,
          text: message,
        })
      },
      fetchMetadataSchemas() {
        let options = {
          successCb: () => null,
          failedCb: err => {
            console.error(err)
            this.emitNotification(
              'error',
              'Error occurred fetching metadat schema'
            )
          },
        }
        this.getMetadataSchemas(options)
      },
      handleFileInput(event) {
        let uploadedFileName = ''
        if (event && event.srcElement && event.srcElement.files) {
          uploadedFileName = event.srcElement.files[0].name
          if (this.filename == '' || this.filename == null) {
            this.filename = uploadedFileName
          }
        }
      },
      startUpload() {
        this.selectedFileName = this.$refs.myUpload.$el.querySelector('input[type=file]').files[0].name
        if(this.isFileNameAutoFill){
          this.filename = this.selectedFileName
        }
        this.isUploading = true
      },
      uploadFailed (error) {
        this.resetUploadComponent()
        this.emitNotification(
              'error',
              error.response.data.ExceptionMessage
          )
      },
      fileUploaded() {
        this.isUploading = false
        this.isUploadCompleted = true
      },
      async uploadPrepare(event){
        var vm = this     
        if(event.target.tagName != 'INPUT' && !vm.isUploading){
          vm.resetUploadComponent()
          event.preventDefault()
          let options = {
            successCb: (res) => { 
              vm.uploadFileIdentifier = res
              vm.$refs.myUpload.$el.querySelector('input[type=file]').dispatchEvent(new MouseEvent('click'))   
            },
            failedCb: (err) => {
              console.log(err)
              this.emitNotification(
                'error',
                'Error occurred fetching upload id'
              )
            },
          }
          await this.getUploadIdentifier(options)
        }
      },
      resetUploadComponent() {
        this.isUploading = false
        this.isUploadCompleted = false
        this.uploadFileIdentifier = null
        this.selectedFileName = null
        this.componentKey = Date.now()
      },
      closeDialog () {        
        if (this.isUploading || this.isUploadCompleted) {
          this.exitConfirmDialog = true
        } else {
          this.clearForm()
        }
      },
      closeAutoDetectResult () {        
        this.clearForm()
        this.$emit('refresh')
      },
      cleanUpUploadedFile() {
        if(this.isUploading) {
          this.$refs.myUpload.cancelUpload()
          this.uploadCancel({uploadFileIdentifier: this.uploadFileIdentifier})
        } else if(this.isUploadCompleted) {
          this.uploadCancel({uploadFileIdentifier: this.uploadFileIdentifier})
        }
        this.clearForm()
      },
      emitMetadataInput() {
        let that = this
        this.$nextTick(() => { 
          if(that.$refs.form_step2) 
          {
            that.$refs.form_step2.validate()
          }
        }) 
      },
    },
    
    mounted() {
      this.resetUploadComponent()
      this.fetchMetadataSchemas()
    },
  }
</script>

<style lang="stylus" scoped>
  form {
    width: 100%;
  }
</style>
